import React from 'react';
import { Palette, Settings } from 'lucide-react';
import { QRCodeData } from '../types/qr-types';
import { t } from '../i18n/translations';

interface QRCustomizerProps {
  qrData: QRCodeData;
  onDataChange: (data: QRCodeData) => void;
  language: 'en' | 'zh';
  showCustomizer: boolean;
  onToggleCustomizer: () => void;
}

const QRCustomizer: React.FC<QRCustomizerProps> = ({ 
  qrData, 
  onDataChange, 
  language, 
  showCustomizer, 
  onToggleCustomizer 
}) => {
  const presetColors = [
    '#000000', '#1f2937', '#374151', '#4f46e5', '#7c3aed',
    '#dc2626', '#ea580c', '#d97706', '#059669', '#0891b2'
  ];

  const presetBackgrounds = [
    '#ffffff', '#f9fafb', '#f3f4f6', '#fef3c7', '#fce7f3',
    '#dbeafe', '#e0e7ff', '#f0f9ff', '#ecfdf5', '#f0fdfa'
  ];

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">{t('customize', language)}</h3>
        <button
          onClick={onToggleCustomizer}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <Settings className="w-5 h-5 text-white" />
        </button>
      </div>
      
      {showCustomizer && (
        <div className="space-y-6">
          {/* Colors */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              <Palette className="w-4 h-4 inline mr-2" />
              {t('qrCodeColor', language)}
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {presetColors.map((color) => (
                <button
                  key={color}
                  onClick={() => onDataChange({ ...qrData, color })}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${
                    qrData.color === color ? 'border-white scale-110' : 'border-white/30 hover:border-white/60'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <input
              type="color"
              value={qrData.color}
              onChange={(e) => onDataChange({ ...qrData, color: e.target.value })}
              className="w-full h-10 rounded-lg border border-white/20 bg-transparent cursor-pointer"
            />
          </div>

          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              {t('backgroundColor', language)}
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {presetBackgrounds.map((color) => (
                <button
                  key={color}
                  onClick={() => onDataChange({ ...qrData, backgroundColor: color })}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${
                    qrData.backgroundColor === color ? 'border-white scale-110' : 'border-white/30 hover:border-white/60'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <input
              type="color"
              value={qrData.backgroundColor}
              onChange={(e) => onDataChange({ ...qrData, backgroundColor: e.target.value })}
              className="w-full h-10 rounded-lg border border-white/20 bg-transparent cursor-pointer"
            />
          </div>

          {/* Size */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              {t('size', language)}: {qrData.size}px
            </label>
            <input
              type="range"
              min="128"
              max="512"
              step="32"
              value={qrData.size}
              onChange={(e) => onDataChange({ ...qrData, size: parseInt(e.target.value) })}
              className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>128px</span>
              <span>512px</span>
            </div>
          </div>

          {/* Error Correction Level */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              {t('errorCorrectionLevel', language)}
            </label>
            <select
              value={qrData.errorLevel}
              onChange={(e) => onDataChange({ ...qrData, errorLevel: e.target.value as 'L' | 'M' | 'Q' | 'H' })}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="L">{t('errorLevelLow', language)}</option>
              <option value="M">{t('errorLevelMedium', language)}</option>
              <option value="Q">{t('errorLevelQuartile', language)}</option>
              <option value="H">{t('errorLevelHigh', language)}</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              {t('errorLevelDesc', language)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCustomizer;